import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import verseImage from './verse-canvas.jpg';

const features = [
  {
    title: 'From Visuals to Verses',
    content:
      'Cross-medium generation enables the translation of prompts, phrases, or visual references into original works of your chosen modality. VerseCanvas reinterprets written input as artwork and visual input as poetry, expanding creative potential through interdisciplinary transformation.',
  },
  {
    title: 'Creative Output Controls',
    content:
      'Shape the character of your work with customizable presets. Select from linguistic forms, tonal registers, visual aesthetics, color palettes, and simulated tools.',
  },
  {
    title: 'Share, Publish, & Promote Your Work',
    content:
      'Transfer completed projects directly to your InkLink profile feed, formal portfolio, or submit them in an application to be published.',
  },
];

const FeatureVerseCanvas: React.FC = () => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [clicked, setClicked] = useState<number | null>(null);

  return (
    <Box sx={{ 
      backgroundColor: 'transparent', 
      minHeight: '100vh', 
      py: { xs: 4, md: 8 },
      px: { xs: 2, sm: 4, md: 0, xl: 8 }
    }}>
      <Grid
        container
        spacing={{ xs: 3, md: 6 }}
        sx={{
          height: '100%',
          width: '100%',
          margin: 0,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        {/* Image Column */}
					<Grid item xs={12} md={6} sx={{ order: { xs: 0, md: 0 } }}>
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							sx={{
								height: {
									xs: '300px', // Fixed height for mobile
									sm: '400px',
									md: '500px',
									lg: '600px',
								},
								width: '100%',
								mt: { xs: 0, md: 0 },
								mb: { xs: 4, md: 0 }, // Bottom margin on mobile
								overflow: 'hidden',
							}}
						>
							<Box
								component="img"
								src={verseImage}
								alt="VerseCanvas abstract art"
								sx={{
									width: '100%',
									height: '100%',
									objectFit: 'cover',
									borderRadius: { xs: 1, md: 2 },
									boxShadow: { xs: 2, md: 3 },
								}}
							/>
						</Box>
					</Grid>

        {/* Right Content */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2, sm: 3, md: 6 }, // Increased padding on desktop for more white space
            py: { xs: 0, md: 3 }, // Remove top padding on mobile
            overflowY: 'auto',
            order: { xs: 1, md: 1 }, // Text comes second on mobile
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              lineHeight: { xs: 1.2, md: 1.3 }
            }}
          >
            VerseCanvas AI
          </Typography>

          <Typography 
            variant="h5" 
            sx={{ 
              mt: { xs: 1.5, md: 2 }, 
              fontWeight: 'bold',
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
              lineHeight: { xs: 1.3, md: 1.4 },
              width: { xs: '65vw', md: '100vw' },
            }}
          >
            Seamless AI-generated art & literature
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: { xs: '1rem', md: '1.1rem' }, 
              mb: { xs: 3, md: 3 },
              lineHeight: { xs: 1.4, md: 1.5 },
              width: { xs: '65vw', md: '100vw' },
            }}
          >
            Join the new wave where creativity meets intelligent generation. <br />
            Enhance and redefine the possibilities of artistic expression.
          </Typography>

          <Typography 
            variant="body2" 
            sx={{ 
              mb: { xs: 2, md: 2 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              maxWidth: { xs: '65ch', md: '65ch' }
            }}
          >
            VerseCanvas AI is a multimodal creative engine powered by advanced generative models. Drawing from prompts, visual inputs, and user-defined parameters, the system generates poetry and artwork aligned with project-specific goals. By combining algorithmic precision with flexible stylistic control, VerseCanvas AI enables the development of structured, high-quality creative outputs. Built for cross-disciplinary application, the tool supports both experimental and targeted artistic workflows.
          </Typography>

          {/* Collapsibles */}
          {features.map((item, idx) => {
            const isOpen = clicked === idx || hovered === idx; // Open on click or hover
            return (
              <Box
                key={idx}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  mt: { xs: 1.5, md: 2 },
                  backgroundColor: 'transparent',
                  borderRadius: 2,
                  transition: 'all 0.3s ease-in-out',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setHovered(idx)} // Desktop hover
                onMouseLeave={() => setHovered(null)} // Desktop hover
                onClick={() => {
                  // Mobile/Desktop click toggle
                  if (clicked === idx) {
                    setClicked(null); // Close if already open
                  } else {
                    setClicked(idx); // Open this one
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: { xs: 44, md: 'auto' }, // Minimum touch target size
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      lineHeight: { xs: 1.3, md: 1.4 },
                      maxWidth: { xs: '80%', md: '85%' } // Prevent text from overlapping button
                    }}
                  >
                    {item.title}
                  </Typography>
                  <IconButton
                    size="small"
                    disableRipple
                    sx={{
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease-in-out',
                      minWidth: { xs: 44, md: 'auto' }, // Minimum touch target size
                      minHeight: { xs: 44, md: 'auto' },
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 1,
                      fontSize: { xs: '0.85rem', md: '0.9rem' },
                      lineHeight: { xs: 1.4, md: 1.5 },
                      maxWidth: { xs: '100%', md: '95%' }
                    }}
                  >
                    {item.content}
                  </Typography>
                </Collapse>
              </Box>
            );
          })}
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeatureVerseCanvas;
